import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, X } from 'lucide-react';
import { getTodayChecklist, type ChecklistType } from '@/lib/api/checklists.js';
import {
  dismissBanner,
  isBannerDismissed,
  resolveActiveChecklistType,
} from '@/lib/checklist-defaults.js';
import { cn } from '@/lib/utils.js';

function ChecklistBannerRow({
  type,
  onOpen,
}: {
  type: ChecklistType;
  onOpen: (type: ChecklistType) => void;
}) {
  const query = useQuery({
    queryKey: ['checklist-today', type],
    queryFn: () => getTodayChecklist(type),
    staleTime: 60_000,
  });

  const checklist = query.data;
  const [dismissed, setDismissed] = useState(() =>
    checklist?.date ? isBannerDismissed(type, checklist.date) : false,
  );

  // Disabled for store (API returns enabled: false / empty list)
  if (checklist && 'enabled' in checklist && checklist.enabled === false) return null;
  if (!checklist || dismissed) return null;
  if (!checklist.id) return null;
  const { progress } = checklist;
  if (progress.total <= 0) return null;
  if (progress.completed >= progress.total) return null;

  const isMorning = type === 'open';
  const remaining = progress.total - progress.completed;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2.5 text-sm sm:px-6 lg:px-8',
        isMorning
          ? 'border-amber-200 bg-amber-50 text-amber-950'
          : 'border-slate-300 bg-slate-800 text-white',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ClipboardList className="h-4 w-4 shrink-0" />
        <p className="font-medium">
          {isMorning ? 'Morning checklist' : 'End-of-day checklist'} — {progress.completed}/
          {progress.total} done
          {remaining > 0 ? ` · ${remaining} remaining` : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onOpen(type)}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-bold',
            isMorning ? 'bg-amber-700 text-white hover:bg-amber-800' : 'bg-white text-slate-900 hover:bg-slate-100',
          )}
        >
          Open checklist
        </button>
        <button
          type="button"
          onClick={() => {
            dismissBanner(type, checklist.date);
            setDismissed(true);
          }}
          className={cn(
            'rounded-lg p-1.5',
            isMorning ? 'text-amber-800 hover:bg-amber-100' : 'text-slate-300 hover:bg-slate-700',
          )}
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function ChecklistBanner({
  onOpen,
  openingEnabled = true,
  closingEnabled = true,
}: {
  onOpen: (type: ChecklistType) => void;
  openingEnabled?: boolean;
  closingEnabled?: boolean;
}) {
  // Exactly one active type — never both open and close prompts at once
  const active = resolveActiveChecklistType({ openingEnabled, closingEnabled });
  if (!active) return null;
  return <ChecklistBannerRow type={active} onOpen={onOpen} />;
}

export function useChecklistHeaderBadge(opts?: {
  openingEnabled?: boolean;
  closingEnabled?: boolean;
}) {
  const type = resolveActiveChecklistType({
    openingEnabled: Boolean(opts?.openingEnabled),
    closingEnabled: Boolean(opts?.closingEnabled),
  });
  const query = useQuery({
    queryKey: ['checklist-today', type],
    queryFn: () => getTodayChecklist(type!),
    enabled: Boolean(type),
    refetchInterval: 120_000,
  });
  const progress = query.data?.progress ?? { completed: 0, total: 0 };
  const disabledByServer = query.data && 'enabled' in query.data && query.data.enabled === false;
  return {
    type: type ?? ('open' as ChecklistType),
    progress: disabledByServer || !query.data?.id ? { completed: 0, total: 0 } : progress,
    loading: query.isLoading,
    active: Boolean(type && query.data?.id && !disabledByServer && (progress.total > 0 || query.isLoading)),
  };
}
